// AC GUIDE 8: WHAT SIZE AC DO I NEED FOR MY ROOM? (29 Oct 2026)
// /blog/what-size-ac-do-i-need
//
// Written to GUIDES_PLAYBOOK.md section 3, in the new layout (guide 7
// onwards): the answer, the animation, the floor x room-size grid, one rupee
// example, do's and don'ts, short parts, where to see it, questions.
//
// THE ADVICE IS SAHEB'S (Oct 2026): size for the hottest Delhi afternoon the
// way Daikin's heat-load sheet does; take the average case for the rooms
// around the room; take 5% off an AC's cooling for every °C above its
// full-cooling temperature; a 1 ton AC will not cool a 144 sq ft top-floor
// room in a Delhi summer; on a top floor a non-inverter usually makes more
// sense, and if the buyer wants an inverter there, the 5 Star.
//
// NO SIZE IS TYPED HERE. Every ton in the grid, the animation, the example and
// the questions is worked out by the AC calculator's own engine
// (lib/heatLoad.js, data/indiaClimate.js, roomNeed() and answerFor() in
// data/acCalculatorContent.js), so this page can never give a size the
// calculator would not. Callers pass the engine in. Prices come live from the
// catalogue via pickGuideModels(). No imports: prerender.js loads this file by
// stripping `export`. **double stars** mean bold.

export const SIZE_SLUG = 'blog/what-size-ac-do-i-need';
export const SIZE_PUBLISHED = '2026-10-29';
export const SIZE_UPDATED = '2026-10-29';

export const SIZE_SEO = {
  title: 'What Size AC Do I Need for My Room? Ton by Room Size | Citi Cool',
  meta: 'What size AC for your room? A 1.5 ton for most Delhi bedrooms up to 12 × 14 ft. On a top floor, a 1.5 ton 5 Star or bigger. See the size by room and floor.',
  eyebrow: 'AC Guide · Buying',
  h1: 'What size AC do I need for my room?',
  crumb: 'AC size for your room',
  readMins: 4,
};

// The grid: a Delhi bedroom, two people, afternoon sun on the window, room
// held at 24°C, on the hottest afternoon of an average year.
export const SIZE_FLOORS = [
  { key: 'ground', label: 'Ground floor', short: 'Ground' },
  { key: 'mid', label: 'Middle floor', short: 'Middle' },
  { key: 'top', label: 'Top floor', short: 'Top' },
];
export const SIZE_ROOMS = [[10, 12], [12, 12], [12, 14], [14, 14]];
const SUN = 'W';

// ─── sources ──────────────────────────────────────────────────────────────

export const SIZE_SOURCES = [
  { key: 'saheb', label: 'Our showroom\'s sizing advice: size for the hottest afternoon, take the average case for the rooms around yours, and a 1 ton will not cool a 144 sq ft top-floor room in a Delhi summer (October 2026)', by: 'Citi Cool', href: null },
  { key: 'saheb-top', label: 'Our showroom\'s advice for top floors: a non-inverter usually makes more sense; if you want an inverter, a 5 Star (October 2026)', by: 'Citi Cool', href: null },
  { key: 'calc', label: "Our AC calculator: Daikin's heat-load method, on IS 7896 design weather (Bureau of Indian Standards) and IMD's hottest day of an average year", by: 'Citi Cool engineering', href: '/ac-calculator' },
  { key: 'derate', label: "Daikin's full-cooling outside temperature for each series (5 Star 46°C, 3 Star 43°C), and 5% less cooling for every °C above it, as used in our AC calculator", by: 'Daikin · Citi Cool engineering', href: '/ac-calculator' },
  { key: 'daikin-ton', label: "What does 'Ton' mean in air conditioning and why is it important?", by: 'Daikin India', href: 'https://daikinindia.com/blog-details/what-does-ton-mean-air-conditioning-and-why-it-important' },
  { key: 'units-guide', label: 'How many units does a 1.5 ton AC use in an hour?', by: 'Citi Cool', href: '/blog/ac-power-consumption-units-per-hour' },
  { key: 'stars-guide', label: '3 Star vs 5 Star AC: is a 5 Star worth it?', by: 'Citi Cool', href: '/blog/3-star-vs-5-star-ac' },
  { key: 'prices', label: 'Daikin AC prices and stock today', by: 'citicool.in', href: '/daikin-ac-price-list-delhi' },
];

// ─── working out the sizes ────────────────────────────────────────────────

const tonText = (s) => `${Number(s) % 1 === 0 ? Number(s).toFixed(0) : Number(s)}\u00a0ton`;
const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

// One answer from answerFor() as the page says it: a size, plus "5 Star" when
// only the 5 Star models of that size keep up. If what keeps up cannot be said
// that simply, the next size up. null if neither works (the build stops).
//   units: unitsFrom(); stars: { [model id]: stars }
function simple(ans, units, stars) {
  if (!ans || ans.kind !== 'single') return null;
  const say = (size, ok) => {
    const all = units.filter((u) => u.soldAs === size);
    if (!ok.length) return null;
    if (ok.length === all.length) return { size, five: false };
    const five = all.filter((u) => stars[u.id] >= 5);
    if (ok.every((u) => stars[u.id] >= 5) && five.every((u) => ok.includes(u))) return { size, five: true };
    return null;
  };
  return say(ans.size, ans.ok) || (ans.next != null ? say(ans.next, ans.okNext) : null);
}
export const cellText = (c) => (c ? `${tonText(c.size)}${c.five ? ' 5\u00a0Star' : ''}` : 'Use the calculator');
const a = (c) => `a ${cellText(c)}`;

// Everything the page needs from the engine.
//   calc = { roomNeed, answerFor } from data/acCalculatorContent.js
//   engine = the calculator's ENGINE; units = unitsFrom(CATALOGUE, SERIES)
//   catalogue = CATALOGUE (for star ratings)
export function sizeData(calc, engine, units, catalogue) {
  const stars = Object.fromEntries(catalogue.map((m) => [m.id, m.stars]));
  const cell = (args) => simple(calc.answerFor(engine, calc.roomNeed(engine, args), units), units, stars);
  const grid = SIZE_FLOORS.map((f) => ({
    ...f,
    cells: SIZE_ROOMS.map(([l, w]) => cell({ l, w, floor: f.key, sun: SUN })),
  }));
  const at = (floor, l, w) => grid.find((r) => r.key === floor).cells[SIZE_ROOMS.findIndex((x) => x[0] === l && x[1] === w)];

  const peakC = engine.climateFor(engine.STATIONS.delhi).peakC;
  const top144 = calc.roomNeed(engine, { l: 12, w: 12, floor: 'top', sun: SUN });
  const mid168 = calc.roomNeed(engine, { l: 12, w: 14, floor: 'mid', sun: SUN });
  const top168 = calc.roomNeed(engine, { l: 12, w: 14, floor: 'top', sun: SUN });
  const cap = (u) => engine.capacityTR(u.kw, u.full, peakC);
  const oneTon = units.filter((u) => u.soldAs === 1);
  const bestOne = Math.max(...oneTon.map(cap));
  const three = units.filter((u) => stars[u.id] === 3 && u.inverter);
  const five = units.filter((u) => stars[u.id] >= 5);

  return {
    grid,
    at,
    peakC,
    topMore: Math.round((top168.loadTR / mid168.loadTR - 1) * 100),          // % more cooling on a top floor
    threeLoss: Math.round((1 - engine.capacityTR(1, 43, peakC) / (1 / engine.KW_PER_TR)) * 100),
    threeFull: [...new Set(three.map((u) => u.full))],
    fiveFull: [...new Set(five.map((u) => u.full))],
    oneTonShare: Math.floor((bestOne / top144.summerTR) * 100),            // best 1 ton, top-floor 12 x 12
    top144,
    sqft200: { mid: cell({ area: 200, floor: 'mid', sun: SUN }), top: cell({ area: 200, floor: 'top', sun: SUN }) },
    blr144: cell({ l: 12, w: 12, floor: 'mid', sun: SUN, city: 'bengaluru' }),
    // Does a given unit keep a top-floor 12 x 12 room cool on that afternoon?
    fitsTop144: (id) => {
      const u = units.find((x) => x.id === id);
      return Boolean(u && engine.fits(u, top144));
    },
    shareTop144: (id) => {
      const u = units.find((x) => x.id === id);
      return u ? Math.floor((cap(u) / top144.summerTR) * 100) : 0;
    },
  };
}

// The page's words rest on these. If a change to the catalogue or the engine
// breaks one, the build stops instead of publishing a wrong answer.
export function sizeChecks(d) {
  const fails = [];
  const is = (c, size, five) => c && c.size === size && c.five === five;
  ['10', '12x12', '12x14'].forEach((k, i) => {
    const [l, w] = SIZE_ROOMS[i];
    if (!is(d.at('mid', l, w), 1.5, false)) fails.push(`middle floor ${l} x ${w} is not a plain 1.5 ton (${k})`);
  });
  if (!is(d.at('top', 12, 12), 1.5, true)) fails.push('top floor 12 x 12 is not a 1.5 ton 5 Star');
  const t14 = d.at('top', 12, 14);
  if (!t14 || t14.size <= 1.5) fails.push('top floor 12 x 14 does not need more than 1.5 ton');
  if (d.oneTonShare >= 100) fails.push('a 1 ton keeps up in a top-floor 12 x 12 room');
  if (d.threeFull.join() !== '43' || d.fiveFull.join() !== '46') fails.push('3 Star are not all 43°C, or 5 Star not all 46°C');
  return fails;
}

// ─── the page ─────────────────────────────────────────────────────────────

// d: sizeData(); five15: the 1.5 ton 5 Star and three18: the 1.8 ton 3 Star,
// from pickGuideModels() (live price and stock).
export function sizeContent(d, five15, three18) {
  const peak = Math.round(d.peakC);
  const fiveShare = five15 ? d.shareTop144(five15.model.id) : 0;
  const okFive = five15 && d.fitsTop144(five15.model.id);
  const okThree18 = three18 && d.fitsTop144(three18.model.id);

  const say200 = (() => {
    const { mid, top } = d.sqft200;
    const head = mid && mid.size <= 1.5 ? 'Yes, on a middle floor' : 'No';
    return `${head}: buy ${a(mid)}. On a top floor, buy ${a(top)}.`;
  })();
  const ten = { g: d.at('ground', 10, 12), m: d.at('mid', 10, 12) };
  const say1012 = (() => {
    if (ten.m && ten.m.size <= 1) return `Yes, ${a(ten.m)} is enough on a middle floor.`;
    if (ten.g && ten.g.size <= 1) return `Only on a ground floor, and only ${a(ten.g)}. On a middle or top floor, buy ${a(ten.m)}.`;
    return `No. Buy ${a(ten.m)}.`;
  })();

  return {
    answer: {
      text: '**Buy a 1.5 ton AC for a Delhi bedroom up to 12 × 14 ft. On a top floor, buy a 1.5 ton 5 Star up to 12 × 12 ft. Above that, buy a bigger AC.**',
      src: ['saheb', 'calc'],
    },
    because: [
      { text: `A top-floor room needs about ${d.topMore}% more cooling. Its roof bakes in the sun all day.`, src: ['calc'] },
      { text: `On a ${peak}°C Delhi afternoon, a 3 Star loses about ${d.threeLoss}% of its cooling. A 5 Star keeps it all.`, src: ['derate'] },
      { text: 'A 1 ton AC will not cool a 12 × 12 ft top-floor room in summer.', src: ['saheb'] },
    ],
    watch: {
      title: 'Watch: a 1 ton and a 1.5 ton 5 Star AC in a top-floor room',
      captions: [
        { at: 0, text: 'A top-floor bedroom, 12 × 12 ft, on Delhi\'s hottest afternoon.' },
        { at: 0.2, text: 'The sun bakes the roof. Heat keeps coming into the room.' },
        { at: 0.45, text: `A 1 ton AC runs at full power. It gives only about ${d.oneTonShare}% of the cooling the room needs.` },
        { at: 0.75, text: 'A 1.5 ton 5 Star gives all of it. The room reaches 24°C.' },
      ],
      note: `A simple drawing, worked out with our AC calculator and Daikin's method. The day is ${peak}°C, the hottest of an average Delhi year.`,
      noteSrc: ['calc', 'derate'],
      oneTon: d.oneTonShare,
      five: Math.min(100, fiveShare),
    },
    grid: {
      title: 'AC size by room size and floor',
      cols: SIZE_ROOMS.map(([l, w]) => ({ short: `${l}×${w}`, note: `${l * w} sq ft` })),
      rows: d.grid.map((r) => ({
        label: r.label,
        short: r.short,
        cells: r.cells.map((c) => ({ head: cellText(c), tone: c && c.five ? 'green' : 'gray' })),
      })),
      rule: '**5 Star** means only a 5 Star of that size keeps up. A bedroom for two, with afternoon sun on the window, on Delhi\'s hottest day.',
      src: ['calc', 'derate'],
    },
    example: okFive && okThree18 ? {
      title: 'In rupees: one real example',
      lines: [
        { label: `1.5 ton 5 Star (${five15.code}), our price today`, value: inr(five15.price) },
        { label: `1.8 ton 3 Star (${three18.code}), our price today`, value: inr(three18.price) },
      ],
      text: 'Both keep a 12 × 12 ft top-floor bedroom cool on Delhi\'s hottest day. A 1 ton costs less, but it cannot.',
      src: ['prices', 'calc'],
    } : null,
    sides: [
      {
        key: 'dos',
        title: 'Choosing an AC size: do\'s and don\'ts',
        goodHead: 'Do',
        badHead: 'Don\'t',
        good: [
          { text: 'Measure the room\'s length and width in feet.', src: ['calc'] },
          { text: 'Go bigger on a top floor, or with afternoon sun on the window.', src: ['calc'] },
          { text: 'On a top floor, choose a 5 Star if you want an inverter.', src: ['saheb-top'] },
          { text: 'Use our [AC calculator](/ac-calculator) for a living room, office or kitchen.', src: ['calc'] },
        ],
        bad: [
          { text: 'Don\'t buy a 1 ton for a 12 × 12 ft top-floor room.', src: ['saheb'] },
          { text: 'Don\'t buy a much bigger AC than you need. It is not efficient.', src: ['daikin-ton'] },
          { text: 'Don\'t size by the room alone. Floor and sun change the answer.', src: ['saheb', 'calc'] },
        ],
      },
    ],
    parts: [
      {
        id: 'ton',
        q: 'What does ton mean on an AC?',
        bullets: [
          { text: 'Ton tells you how much heat an AC can take out of a room.', src: ['daikin-ton'] },
          { text: 'It has nothing to do with the AC\'s weight.', src: ['daikin-ton'] },
          { text: 'Think of a water pump. A bigger pump empties a tank faster.' },
        ],
      },
      {
        id: 'top',
        q: 'Why does a top-floor room need a bigger AC?',
        bullets: [
          { text: 'The roof bakes in the sun all day.', src: ['calc'] },
          { text: 'It keeps giving off heat into the night.', src: ['calc'] },
          { text: `A 12 × 14 ft bedroom needs about ${d.topMore}% more cooling on the top floor.`, src: ['calc'] },
          { text: 'It uses more units too. See [how many units an AC uses in an hour](/blog/ac-power-consumption-units-per-hour).', src: ['units-guide'] },
        ],
      },
      {
        id: 'heat',
        q: 'Why does a 5 Star AC keep up on the hottest days?',
        bullets: [
          { text: 'Daikin\'s 5 Star ACs keep full cooling up to 46°C outside. 3 Star ACs, up to 43°C.', src: ['derate'] },
          { text: 'Above that, an AC loses about 5% of its cooling for each degree.', src: ['derate', 'saheb'] },
          { text: `Delhi's hottest afternoon reaches about ${peak}°C. So a 3 Star loses about ${d.threeLoss}%.`, src: ['calc'] },
          { text: 'Is the extra price worth it? Read [3 Star vs 5 Star AC](/blog/3-star-vs-5-star-ac).', src: ['stars-guide'] },
        ],
      },
      {
        id: 'small',
        q: 'What happens if your AC is too small?',
        bullets: [
          { text: 'It runs at full power almost all the time.', src: ['saheb'] },
          { text: 'The room never reaches the temperature you set.', src: ['saheb'] },
          { text: 'An inverter saves nothing then. Your bill is higher than the label says.', src: ['saheb'] },
          { text: 'On a top floor, a non-inverter often makes more sense. Read [inverter or non-inverter](/blog/inverter-vs-non-inverter-ac).', src: ['saheb-top'] },
        ],
      },
      {
        id: 'big',
        q: 'Is a bigger AC always better?',
        bullets: [
          { text: 'No. Daikin says an AC should match the room. Too big is not efficient.', src: ['daikin-ton'] },
          { text: 'A bigger AC also costs more to buy.', src: ['prices'] },
          { text: 'Pick the smallest size that keeps up on the hottest day.', src: ['calc'] },
        ],
      },
    ],
    fits: [
      { label: 'Most bedrooms', title: 'Daikin 1.5 ton ACs', text: 'Today\'s prices and stock.', href: '/daikin-1-5-ton-ac-price', src: ['prices'] },
      { label: 'Top floors', title: 'Daikin 1.5 ton 5 Star ACs', text: 'Full cooling up to 46°C outside.', href: '/daikin-1-5-ton-5-star-ac-price', src: ['prices'] },
      { label: 'Bigger rooms', title: 'Daikin 1.8 ton ACs', text: 'For bigger or top-floor rooms.', href: '/daikin-1-8-ton-ac-price', src: ['prices'] },
      { label: 'Your exact room', title: 'AC size calculator', text: 'Any room, any floor, 50+ cities.', href: '/ac-calculator', src: ['calc'] },
    ],
    faq: [
      {
        q: 'What size AC do I need for a 12 × 12 room?',
        a: `In Delhi, ${a(d.at('mid', 12, 12))} on a middle floor. On a top floor, ${a(d.at('top', 12, 12))}.`,
        src: ['calc'],
      },
      {
        q: 'Is a 1 ton AC enough for a 10 × 12 room?',
        a: say1012,
        src: ['calc'],
      },
      {
        q: 'Is a 1.5 ton AC enough for 200 sq ft?',
        a: say200,
        src: ['calc'],
      },
      {
        q: 'Will a 1 ton AC cool a top-floor room?',
        a: `Not a 12 × 12 ft room in a Delhi summer. Even the best 1 ton gives about ${d.oneTonShare}% of the cooling it needs.`,
        src: ['saheb', 'calc'],
      },
      {
        q: 'How many units does a 1.5 ton AC use in an hour?',
        a: 'About half a unit on the label. Two to three times that on a top floor. [Read the full guide](/blog/ac-power-consumption-units-per-hour).',
        src: ['units-guide'],
      },
      {
        q: 'Does the city change the AC size?',
        a: `Yes. A 12 × 12 ft middle-floor bedroom needs ${a(d.at('mid', 12, 12))} in Delhi. In Bengaluru, ${a(d.blr144)}. Check yours in the [AC calculator](/ac-calculator).`,
        src: ['calc'],
      },
    ],
  };
}
